import { useCssModule, computed, onBeforeUnmount, ref, nextTick, onMounted, watch, useSlots } from "vue";
import { u as useI18n } from "./assets/index-CgZzOVq6.js";
import "vue-demi";
import { u as useDataTestIdProp } from "./assets/index-BNd8MWnO.js";
import { u as useLoadingProp } from "./assets/index-D0Qy8ijJ.js";
import "./assets/index-vzP2cezV.js";
import "vue-router";
import "dompurify";
import { useElementSize, whenever, onKeyStroke } from "@vueuse/core";
import XSpinner from "./XSpinner.js";
import { X as XIcon } from "./assets/XIcon-U9bwokTN.js";
import { n as normalizeComponent } from "./assets/_plugin-vue2_normalizer-VvYS6rEj.js";
import "vue-i18n";
import "./locales.js";
import "@fortawesome/vue-fontawesome";
const useBackdropOpacities = {
  none: "transparent",
  low: "var(--color-transparent-1000-medium)",
  high: "var(--color-transparent-1000-high)"
};
const { getLoadingClass } = useLoadingProp();
function useModalLayout(props) {
  const $style = useCssModule();
  const { loadingClass } = getLoadingClass(props);
  const computedModalClasses = computed(() => {
    return [loadingClass.value, ...props.modalClasses];
  });
  const backdropStyles = computed(() => ({
    backgroundColor: useBackdropOpacities[props.backdropOpacity]
  }));
  const modalStyles = computed(() => {
    if (props.fullScreen) {
      return {};
    }
    return {
      width: `${props.width}px`
    };
  });
  function showBodyScrollbar(show = true) {
    document.body.classList[show ? "remove" : "add"]($style.hideScrollbar);
  }
  onBeforeUnmount(() => {
    showBodyScrollbar();
  });
  return {
    modalStyles,
    computedModalClasses,
    backdropStyles,
    showBodyScrollbar
  };
}
function useModalScroll(props) {
  const $style = useCssModule();
  const isScrollOnBottom = ref(false);
  const isScrollOnTop = ref(true);
  const contentRef = ref(null);
  const hasScrollbar = ref(false);
  const { height: contentRefHeight } = useElementSize(contentRef);
  function getIsScrollOnBottom() {
    return contentRef.value.scrollTop + contentRef.value.clientHeight >= contentRef.value.scrollHeight;
  }
  function getIsScrollOnTop() {
    return contentRef.value.scrollTop === 0;
  }
  function getHasScrollBar() {
    var _a, _b;
    return ((_a = contentRef.value) == null ? void 0 : _a.scrollHeight) > ((_b = contentRef.value) == null ? void 0 : _b.clientHeight);
  }
  function checkScrollPosition() {
    isScrollOnBottom.value = getIsScrollOnBottom();
    isScrollOnTop.value = getIsScrollOnTop();
  }
  async function setHasScrollbar() {
    await nextTick();
    hasScrollbar.value = getHasScrollBar();
  }
  const hasShadowTop2 = computed(
    () => props.scrollable && hasScrollbar.value && !isScrollOnTop.value
  );
  const hasShadowBottom2 = computed(
    () => props.scrollable && hasScrollbar.value && !isScrollOnBottom.value
  );
  const customContentClasses = computed(() => ({
    [$style.hasShadowTop]: hasShadowTop2.value,
    [$style.hasShadowBottom]: hasShadowBottom2.value
  }));
  whenever(
    () => props.isOpen && props.scrollable && contentRefHeight.value,
    () => setHasScrollbar()
  );
  return {
    checkScrollPosition,
    customContentClasses,
    contentRef,
    getIsScrollOnTop,
    getIsScrollOnBottom,
    getHasScrollBar,
    setHasScrollbar,
    hasShadowBottom: hasShadowBottom2,
    hasShadowTop: hasShadowTop2,
    contentRefHeight
  };
}
function useModalFocus(props) {
  async function setFocusOnOpen() {
    var _a;
    if (props.focusOnOpen) {
      await nextTick();
      (_a = document.querySelector(props.focusOnOpen)) == null ? void 0 : _a.focus();
    }
  }
  function setFocusOnClose() {
    var _a;
    if (props.focusOnClose) {
      (_a = document.querySelector(props.focusOnClose)) == null ? void 0 : _a.focus();
    }
  }
  return {
    setFocusOnOpen,
    setFocusOnClose
  };
}
function useModalVisibility(props, emit) {
  const { setFocusOnClose, setFocusOnOpen } = useModalFocus(props);
  const { showBodyScrollbar } = useModalLayout(props);
  const isOpenInternal = ref(false);
  const modalData = computed(() => {
    return {
      status: isOpenInternal.value,
      testId: props.dataTestid
    };
  });
  function setIsOpenInternal(newStatus) {
    isOpenInternal.value = newStatus;
  }
  function open() {
    showBodyScrollbar(false);
    setIsOpenInternal(true);
    setFocusOnOpen();
    emit("opened", modalData.value);
  }
  function close() {
    setIsOpenInternal(false);
    showBodyScrollbar();
    setFocusOnClose();
    emit("close", modalData.value);
  }
  function closeBackdrop() {
    if (!props.persistent && !props.fullScreen) {
      close();
    }
  }
  onKeyStroke("Escape", (event) => {
    if (props.isOpen && !props.persistent) {
      event.preventDefault();
      close();
    }
  });
  onMounted(() => {
    if (props.isOpen) {
      open();
    }
  });
  watch(
    () => props.isOpen,
    () => {
      if (props.isOpen) {
        open();
      } else {
        close();
      }
    }
  );
  return {
    isOpenInternal,
    modalData,
    closeBackdrop,
    close
  };
}
const hideScrollbar = "_hideScrollbar_lupfp_1";
const xModalScroll = "_xModalScroll_lupfp_5";
const innerScroll = "_innerScroll_lupfp_26";
const xModalBackdrop = "_xModalBackdrop_lupfp_29";
const xModal = "_xModal_lupfp_5";
const xModalContent = "_xModalContent_lupfp_36";
const fullScreen = "_fullScreen_lupfp_39";
const isLoading = "_isLoading_lupfp_71";
const xModalHeader = "_xModalHeader_lupfp_78";
const xModalHeaderContainer = "_xModalHeaderContainer_lupfp_90";
const xModalTitle = "_xModalTitle_lupfp_95";
const xModalCloseButton = "_xModalCloseButton_lupfp_103";
const xModalCloseButtonIcon = "_xModalCloseButtonIcon_lupfp_113";
const hasShadowTop = "_hasShadowTop_lupfp_125";
const hasShadowBottom = "_hasShadowBottom_lupfp_128";
const xModalActions = "_xModalActions_lupfp_136";
const xModalEnterActive = "_xModalEnterActive_lupfp_144";
const xModalLeaveActive = "_xModalLeaveActive_lupfp_145";
const xModalEnterFrom = "_xModalEnterFrom_lupfp_149";
const xModalLeaveTo = "_xModalLeaveTo_lupfp_150";
const xModalEnterTo = "_xModalEnterTo_lupfp_154";
const xModalLeaveFrom = "_xModalLeaveFrom_lupfp_155";
const style0 = {
  hideScrollbar,
  xModalScroll,
  innerScroll,
  xModalBackdrop,
  xModal,
  xModalContent,
  fullScreen,
  isLoading,
  xModalHeader,
  xModalHeaderContainer,
  xModalTitle,
  xModalCloseButton,
  xModalCloseButtonIcon,
  hasShadowTop,
  hasShadowBottom,
  xModalActions,
  xModalEnterActive,
  xModalLeaveActive,
  xModalEnterFrom,
  xModalLeaveTo,
  xModalEnterTo,
  xModalLeaveFrom
};
const { createLoadingProp } = useLoadingProp();
const { createTestIdProp, setupTestId } = useDataTestIdProp();
const __default__ = {
  name: "XModal",
  components: { XIcon, XSpinner }
};
const _sfc_main = /* @__PURE__ */ Object.assign(__default__, {
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ""
    },
    width: {
      type: [Number, String],
      default: "600"
    },
    closeButton: {
      type: Boolean,
      default: true
    },
    scrollable: {
      type: Boolean,
      default: false
    },
    persistent: {
      type: Boolean,
      default: false
    },
    fullScreen: {
      type: Boolean,
      default: false
    },
    focusOnOpen: {
      type: String,
      default: '.xModal[role="dialog"]'
    },
    focusOnClose: {
      type: String,
      default: ""
    },
    backdropOpacity: {
      type: String,
      default: "low",
      validator: (opacity) => opacity in useBackdropOpacities
    },
    backdropClasses: {
      type: Array,
      default: () => []
    },
    modalClasses: {
      type: Array,
      default: () => []
    },
    headerClasses: {
      type: Array,
      default: () => []
    },
    contentClasses: {
      type: Array,
      default: () => []
    },
    actionsClasses: {
      type: Array,
      default: () => []
    },
    ...createLoadingProp(),
    ...createTestIdProp()
  },
  emits: ["opened", "close", "transition:close"],
  setup(__props, { emit }) {
    const props = __props;
    const slots = useSlots();
    const { t } = useI18n();
    const { formatTestId } = setupTestId(props);
    const { modalStyles, computedModalClasses, backdropStyles } = useModalLayout(props);
    const { isOpenInternal, modalData, closeBackdrop, close } = useModalVisibility(props, emit);
    const { customContentClasses, checkScrollPosition, contentRef } = useModalScroll(props);
    function canShowHeader() {
      return slots.header || props.title || props.closeButton;
    }
    function canShowTitle() {
      return slots.header || props.title;
    }
    function canShowCloseButton() {
      return props.closeButton && !props.persistent;
    }
    return { __sfc: true, createLoadingProp, createTestIdProp, setupTestId, slots, props, emit, t, formatTestId, modalStyles, computedModalClasses, backdropStyles, isOpenInternal, modalData, closeBackdrop, close, customContentClasses, checkScrollPosition, contentRef, canShowHeader, canShowTitle, canShowCloseButton, XSpinner, XIcon };
  }
});
var _sfc_render = function render() {
  var _vm = this, _c = _vm._self._c, _setup = _vm._self._setupProxy;
  return _c("transition", { attrs: { "name": "fade", "enter-active-class": _vm.$style.xModalEnterActive, "leave-active-class": _vm.$style.xModalLeaveActive, "leave-from-class": _vm.$style.xModalLeaveFrom, "leave-to-class": _vm.$style.xModalLeaveTo, "enter-from-class": _vm.$style.xModalEnterFrom, "enter-to-class": _vm.$style.xModalEnterTo }, on: { "after-leave": function($event) {
    return _vm.$emit("transition:close", _setup.modalData);
  } } }, [_c("div", { directives: [{ name: "show", rawName: "v-show", value: _setup.isOpenInternal, expression: "isOpenInternal" }], class: [
    _vm.$style.xModalScroll,
    _vm.scrollable && _vm.$style.innerScroll,
    _vm.fullScreen && _vm.$style.fullScreen
  ], attrs: { "data-testid": _setup.formatTestId() } }, [_c("div", { class: [_vm.$style.xModalBackdrop, ..._vm.backdropClasses], style: _setup.backdropStyles, attrs: { "data-testid": _setup.formatTestId("modalBackdrop") }, on: { "mousedown": function($event) {
    if ($event.target !== $event.currentTarget) return null;
    return _setup.closeBackdrop();
  } } }, [_c("div", { class: [_vm.$style.xModal, ..._setup.computedModalClasses], style: _setup.modalStyles, attrs: { "data-testid": _setup.formatTestId("modalDialog"), "role": "dialog", "aria-modal": "true", "tabindex": "-1" } }, [!_vm.loading ? [_setup.canShowHeader() ? _c("header", { class: [_vm.$style.xModalHeader, ..._vm.headerClasses], attrs: { "data-testid": _setup.formatTestId("header") } }, [_setup.canShowTitle() ? _c("div", { class: _vm.$style.xModalHeaderContainer, attrs: { "data-testid": _setup.formatTestId("headerContainer") } }, [_vm._t("header", function() {
    return [_c("h4", { class: _vm.$style.xModalTitle, attrs: { "data-testid": _setup.formatTestId("title") } }, [_vm._v(" " + _vm._s(_vm.title) + " ")])];
  })], 2) : _vm._e(), _setup.canShowCloseButton() ? _c("button", { class: _vm.$style.xModalCloseButton, attrs: { "data-testid": _setup.formatTestId("closeButton"), "title": _setup.t("ds.xModal.actions.close") }, on: { "click": function($event) {
    return _setup.close();
  } } }, [_vm._t("closeButtonContent", function() {
    return [_c(_setup.XIcon, { class: _vm.$style.xModalCloseButtonIcon, attrs: { "icon-style": "fak", "icon": "close" } })];
  })], 2) : _vm._e()]) : _vm._e(), _setup.slots.default ? _c("section", { ref: "contentRef", class: [_vm.$style.xModalContent, _setup.customContentClasses, ..._vm.contentClasses], attrs: { "data-testid": _setup.formatTestId("content") }, on: { "&scroll": function($event) {
    return _setup.checkScrollPosition.apply(null, arguments);
  } } }, [_vm._t("default")], 2) : _vm._e(), _setup.slots.actions ? _c("footer", { class: [_vm.$style.xModalActions, ..._vm.actionsClasses], attrs: { "data-testid": _setup.formatTestId("actions") } }, [_vm._t("actions")], 2) : _vm._e()] : _vm._t("loadingContent", function() {
    return [_c(_setup.XSpinner, { attrs: { "variant": "light", "data-testid": _setup.formatTestId("loading") } })];
  })], 2)])])]);
};
var _sfc_staticRenderFns = [];
const __cssModules = {
  "$style": style0
};
function _sfc_injectStyles(ctx) {
  for (var key in __cssModules) {
    this[key] = __cssModules[key];
  }
}
var __component__ = /* @__PURE__ */ normalizeComponent(
  _sfc_main,
  _sfc_render,
  _sfc_staticRenderFns,
  false,
  _sfc_injectStyles
);
const XModal = __component__.exports;
export {
  XModal as default
};
